import { GetQuickJournalNow } from "../Clock/QuickJournalClock";
import {
    GetDayHourKey,
    GetDayKeyFromHourKey,
    GetLocalDayHourKey,
    GetLocalDayKeyFromDate,
    GetQuickJournalTodayMidday,
} from "../QuickJournalKeys";
import {
    IQuickJournalEntry,
    IQuickJournalStoredSnapshot,
    numPrevItemsToShow,
    quickJournalHeatmapRows,
} from "../QuickJournalTypes";
import { TRenderable } from "source/app/presentation/Shared/SharedTypes";

export function BuildQuickJournalStoredSnapshot(
    recentEntries: Array<IQuickJournalEntry | TRenderable>,
    weeklyHourlyCounts: Record<string, number> = {}
): IQuickJournalStoredSnapshot {
    const normalizedEntries = NormalizeRecentEntries(recentEntries);
    const normalizedHourlyCounts = PruneQuickJournalHourlyCounts(
        Object.keys(weeklyHourlyCounts).length > 0
            ? weeklyHourlyCounts
            : BuildQuickJournalHourlyCounts(normalizedEntries)
    );

    return {
        recentEntries: normalizedEntries,
        dailyCounts: BuildQuickJournalDayCountsFromHourlyCounts(
            normalizedHourlyCounts
        ),
        weeklyHourlyCounts: normalizedHourlyCounts,
    };
}

export function PruneQuickJournalHourlyCounts(
    counts: Record<string, number>
): Record<string, number> {
    const validDayKeys = new Set<string>();
    const today = GetQuickJournalTodayMidday();

    for (let index = 0; index < quickJournalHeatmapRows; index++) {
        const day = new Date(today);
        day.setDate(today.getDate() - index);
        validDayKeys.add(GetLocalDayKeyFromDate(day));
    }

    return Object.fromEntries(
        Object.entries(counts).filter(([hourKey]) =>
            validDayKeys.has(GetDayKeyFromHourKey(hourKey))
        )
    );
}

export function MergeQuickJournalHourlyCountMaps(
    ...countSets: Array<Record<string, number>>
): Record<string, number> {
    const mergedCounts: Record<string, number> = {};

    countSets.forEach((countSet) => {
        Object.entries(countSet).forEach(([hourKey, count]) => {
            if (!GetDayKeyFromHourKey(hourKey)) {
                return;
            }

            mergedCounts[hourKey] = Math.max(
                mergedCounts[hourKey] ?? 0,
                count
            );
        });
    });

    return PruneQuickJournalHourlyCounts(mergedCounts);
}

function BuildQuickJournalDayCountsFromHourlyCounts(
    hourlyCounts: Record<string, number>
): Record<string, number> {
    const counts: Record<string, number> = {};

    Object.entries(hourlyCounts).forEach(([cellKey, count]) => {
        const dayKey = GetDayKeyFromHourKey(cellKey);

        if (!dayKey) {
            return;
        }

        counts[dayKey] = (counts[dayKey] ?? 0) + count;
    });

    return Object.fromEntries(
        Object.entries(counts).sort(([left], [right]) =>
            left.localeCompare(right)
        )
    );
}

function BuildQuickJournalHourlyCounts(
    recentEntries: Array<IQuickJournalEntry | TRenderable>
): Record<string, number> {
    const counts: Record<string, number> = {};

    recentEntries.filter(IsPersistableQuickJournalEntry).forEach((entry) => {
        const dayHourKey = GetLocalDayHourKey(entry.createdAt || "");

        if (!dayHourKey) {
            return;
        }

        counts[dayHourKey] = (counts[dayHourKey] ?? 0) + 1;
    });

    return counts;
}

function NormalizeRecentEntries(
    entries: Array<IQuickJournalEntry | TRenderable>
): IQuickJournalEntry[] {
    const entriesByKey = new Map<string, IQuickJournalEntry>();

    entries.forEach((entry, index) => {
        if (!IsPersistableQuickJournalEntry(entry)) {
            return;
        }

        const normalizedEntry = NormalizeQuickJournalEntry(entry, index);
        entriesByKey.set(
            `${normalizedEntry.createdAt}_${normalizedEntry.text}`,
            normalizedEntry
        );
    });

    return Array.from(entriesByKey.values())
        .sort(
            (firstEntry, secondEntry) =>
                GetEntrySortTime(firstEntry) - GetEntrySortTime(secondEntry)
        )
        .slice(-numPrevItemsToShow);
}

function NormalizeQuickJournalEntry(
    entry: IQuickJournalEntry,
    index: number
): IQuickJournalEntry {
    const createdAt =
        entry.createdAt && IsValidDate(entry.createdAt)
            ? entry.createdAt
            : GetLegacyCreatedAt(entry.timeStamp, index);

    return {
        text: entry.text,
        timeStamp: entry.timeStamp,
        createdAt,
    };
}

function GetLegacyCreatedAt(timeStamp: string, index: number): string {
    const hours = Number.parseInt(timeStamp.substring(0, 2), 10);
    const minutes = Number.parseInt(timeStamp.substring(2, 4), 10);
    const createdAt = GetQuickJournalNow();

    if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
        createdAt.setHours(hours, minutes, index, 0);
    } else {
        createdAt.setSeconds(createdAt.getSeconds() + index);
    }

    return createdAt.toISOString();
}

function GetEntrySortTime(entry: IQuickJournalEntry): number {
    const createdAt = entry.createdAt || "";
    const dateValue = new Date(createdAt).getTime();

    return Number.isNaN(dateValue) ? 0 : dateValue;
}

function IsValidDate(dateText: string): boolean {
    return !Number.isNaN(new Date(dateText).getTime());
}

function IsPersistableQuickJournalEntry(
    entry: IQuickJournalEntry | TRenderable | null
): entry is IQuickJournalEntry {
    return (
        !!entry &&
        typeof entry === "object" &&
        "text" in entry &&
        "timeStamp" in entry &&
        typeof entry.text === "string" &&
        typeof entry.timeStamp === "string" &&
        (!("createdAt" in entry) || typeof entry.createdAt === "string")
    );
}
